import { useContext } from "react";
import { useReactiveVar } from "@apollo/client";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { AccessContext } from "@me-team/host/src/components/App/App";
import AccessService from "@me-team/host/services/AccessService";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

type UseAddRecordAccessProps = {
    bookingEmployeeId: number | null;
    bookingUuid?: string;
    isBookingSale: boolean;
};

const resolveScopedEmployeeAccess = (
    employeeId: number | string | null | undefined,
    currentBranchEmployeeId: number | string | null | undefined,
    canAll: boolean,
    canSelf: boolean
): boolean => {
    if (!employeeId || !currentBranchEmployeeId) {
        return false;
    }

    if (String(employeeId) === String(currentBranchEmployeeId)) {
        return canSelf || canAll;
    }

    return canAll;
};

export const useAddRecordAccess = ({ bookingEmployeeId, bookingUuid, isBookingSale }: UseAddRecordAccessProps) => {
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);

    const canCreateBookingAll = useRoleAccess(CompanyAccess.LogbookBookingCreate, {
        scope: PermissionScope.All,
    });
    const canCreateBookingSelf = useRoleAccess(CompanyAccess.LogbookBookingCreate, {
        scope: PermissionScope.Self,
    });
    const canEditBookingAll = useRoleAccess(CompanyAccess.LogbookBookingEdit, {
        scope: PermissionScope.All,
    });
    const canEditBookingSelf = useRoleAccess(CompanyAccess.LogbookBookingEdit, {
        scope: PermissionScope.Self,
    });
    const canEditBookingStatus = useRoleAccess(CompanyAccess.LogbookBookingStatusEdit);
    const canViewBookingAll = useRoleAccess(CompanyAccess.LogbookView, {
        scope: PermissionScope.All,
    });
    const canViewBookingSelf = useRoleAccess(CompanyAccess.LogbookView, {
        scope: PermissionScope.Self,
    });
    const canViewHistoryAll = useRoleAccess(CompanyAccess.LogbookHistoryView, {
        scope: PermissionScope.All,
    });
    const canViewHistorySelf = useRoleAccess(CompanyAccess.LogbookHistoryView, {
        scope: PermissionScope.Self,
    });
    const canWriteOffByAccess = useRoleAccess(CompanyAccess.LogbookConsumablesWriteOff);
    const canCreateService = useRoleAccess(CompanyAccess.ServiceCreate);
    const canCreateEmployee = useRoleAccess(CompanyAccess.EmployeeCreate);
    const canViewServiceList = useRoleAccess(CompanyAccess.ServiceListView);
    const canViewEmployeeList = useRoleAccess(CompanyAccess.EmployeeListView);
    const canCreateSaleAll = useRoleAccess(CompanyAccess.WarehouseOperationSaleCreate, {
        scope: PermissionScope.All,
    });
    const canCreateSaleSelf = useRoleAccess(CompanyAccess.WarehouseOperationSaleCreate, {
        scope: PermissionScope.Self,
    });
    const canEditSaleAll = useRoleAccess(CompanyAccess.WarehouseOperationSaleEdit, {
        scope: PermissionScope.All,
    });
    const canEditSaleSelf = useRoleAccess(CompanyAccess.WarehouseOperationSaleEdit, {
        scope: PermissionScope.Self,
    });

    const isAddRecordAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const normalizedCurrentBranchEmployeeId =
        currentBranchEmployeeId !== null && currentBranchEmployeeId !== undefined
            ? Number(currentBranchEmployeeId)
            : null;
    const shouldRestrictCreateBookingToSelf = !bookingUuid && canCreateBookingSelf && !canCreateBookingAll;
    const canEditBookingByAccess = resolveScopedEmployeeAccess(
        bookingEmployeeId,
        normalizedCurrentBranchEmployeeId,
        canEditBookingAll,
        canEditBookingSelf
    );
    const canEditBookingStatusByAccess = canEditBookingStatus;
    const canViewBookingByAccess = resolveScopedEmployeeAccess(
        bookingEmployeeId,
        normalizedCurrentBranchEmployeeId,
        canViewBookingAll,
        canViewBookingSelf
    );
    const canViewHistoryByAccess = resolveScopedEmployeeAccess(
        bookingEmployeeId,
        normalizedCurrentBranchEmployeeId,
        canViewHistoryAll,
        canViewHistorySelf
    );
    const canManageSaleByAccess = resolveScopedEmployeeAccess(
        bookingEmployeeId,
        normalizedCurrentBranchEmployeeId,
        isBookingSale ? canEditSaleAll : canCreateSaleAll,
        isBookingSale ? canEditSaleSelf : canCreateSaleSelf
    );

    const canCreateBookingForEmployee = (employeeId: number | string | null | undefined) =>
        resolveScopedEmployeeAccess(
            employeeId,
            normalizedCurrentBranchEmployeeId,
            canCreateBookingAll,
            canCreateBookingSelf
        );

    return {
        isAddRecordAccessPending,
        normalizedCurrentBranchEmployeeId,
        shouldRestrictCreateBookingToSelf,
        canCreateBookingForEmployee,
        canEditBookingByAccess,
        canEditBookingStatusByAccess,
        canViewBookingByAccess,
        canViewHistoryByAccess,
        canWriteOffByAccess,
        canManageSaleByAccess,
        canCreateService,
        canCreateEmployee,
        canViewServiceList,
        canViewEmployeeList,
    };
};
